import { useCallback } from 'react';
import _ from 'lodash';
import { type SelectFieldValue } from '@gorilla/portal-shared/src/schemas';
import { type SelectFieldDescriptor } from '@gorilla/portal-shared/src/types';
import { CALLOUT_ICON_FROM_TYPE, getCalloutTypeIcon } from '@gorilla/portal-shared/src/core-sections';
import { Box, Flex, Text } from '@vibe/core';
import { dropdownValue } from '@gorilla/ui/src/components/dropdownValue';
import { Dropdown } from '@vibe/core';

type SelectFieldProps = {
  portalSettings: any;
  value: SelectFieldValue | undefined;
  fieldDescriptor: SelectFieldDescriptor;
  sectionFieldValues?: Record<string, unknown>;
  onChange: (value: SelectFieldValue | undefined) => void;
};

export function SelectField({ portalSettings, value, fieldDescriptor, sectionFieldValues, onChange }: SelectFieldProps) {
  const calloutType = (sectionFieldValues?.type as SelectFieldValue | undefined)?.value;
  let normalizedValue = value || fieldDescriptor.defaultValue || { value: undefined };
  let selectedOption = fieldDescriptor.options.find((option) => option.value === normalizedValue.value);

  const labelRenderer = useCallback(
    ({ label, value: optionValue }: { value: any; label: any }) => {
      const isIconFromType = fieldDescriptor.variant === 'icons' && optionValue === CALLOUT_ICON_FROM_TYPE;
      const typeIcon = isIconFromType ? getCalloutTypeIcon(calloutType) : undefined;

      return (
        <Flex gap={8}>
          {fieldDescriptor.variant === 'colored-labels' ? (
            <Box
              style={{
                pointerEvents: 'none',
                overflow: 'hidden',
                width: '12px',
                height: '12px',
                backgroundColor: _.get(portalSettings, `theme.${optionValue === 'general' ? 'primary' : optionValue}Color`) || '#cccccc',
                borderRadius: '100%',
                border: '1px solid #ccc',
                lineHeight: 0,
              }}
            />
          ) : null}
          {fieldDescriptor.variant === 'icons' ? (
            <Box style={{ width: '16px', textAlign: 'center' }}>
              {/* "Auto-chosen" shows the icon the callout's current type resolves to, muted to set it apart */}
              <i
                className={`fa-regular fa-${typeIcon || optionValue}`}
                style={typeIcon ? { color: 'var(--secondary-text-color)' } : undefined}
              ></i>
            </Box>
          ) : null}
          <Box style={{ overflow: 'visible', fontStyle: typeIcon ? 'italic' : undefined }}>{label}</Box>
        </Flex>
      );
    },
    [fieldDescriptor, portalSettings, calloutType],
  );

  return (
    <Box className="section__field section__field--dropdown">
      <Flex
        direction="column"
        align="start"
        gap={6}
        style={{
          marginTop: '2px',
          width: '100%',
        }}
      >
        <Text>{fieldDescriptor.title}</Text>
        <Dropdown
          placeholder={fieldDescriptor.placeholder}
          size="medium"
          clearable={false}
          searchable={false}
          value={dropdownValue(selectedOption)}
          options={fieldDescriptor.options}
          optionRenderer={labelRenderer}
          valueRenderer={labelRenderer}
          onChange={(newCalloutType: any) => {
            if (!newCalloutType) {
              onChange(undefined);
            } else {
              onChange({ value: newCalloutType.value });
            }
          }}
          //className="custom_dropdown"
        />
      </Flex>
    </Box>
  );
}
